import { config } from "../config";
import type {
  Appointment,
  AppointmentsResponse,
  Conversation,
  ConversationsResponse,
  Message,
  MessagesResponse,
  ScheduleAppointmentRequest,
} from "../types/models";
import { ApiClient } from "./client";

const api = new ApiClient(config.conversationApi);

/**
 * Every route below is mounted behind `AuthMiddleware(firebaseAuth, nil)` —
 * claim-less. Role is enforced per handler, and `GET /conversations` branches on
 * userType such that ANY non-homeowner value (including an empty claim) takes
 * the contractor path. Send the force-refreshed token; do not rely on the
 * server to notice a missing claim on the read paths.
 */

export function listConversations(): Promise<Conversation[]> {
  return api.get<ConversationsResponse>("/conversations").then((r) => r.conversations ?? []);
}

export function getConversation(conversationId: string): Promise<Conversation> {
  return api.get<Conversation>(`/conversations/${encodeURIComponent(conversationId)}`);
}

/** Returns newest-first (`createdAt DESC`), unlike the Firestore listener which
 *  the chat thread uses and which is ordered ascending. Used only for the
 *  no-realtime fallback. */
export function getMessageHistory(conversationId: string, limit = 50): Promise<Message[]> {
  return api
    .get<MessagesResponse>(`/conversations/${encodeURIComponent(conversationId)}/messages`, {
      query: { limit },
    })
    .then((r) => r.messages ?? []);
}

export function sendMessage(conversationId: string, content: string): Promise<Message> {
  return api.post<Message>(`/conversations/${encodeURIComponent(conversationId)}/messages`, {
    content,
    messageType: "text",
  });
}

export function markMessagesRead(conversationId: string, messageIds: string[]): Promise<unknown> {
  return api.post(`/conversations/${encodeURIComponent(conversationId)}/messages/read`, {
    messageIds,
  });
}

/** Moves only the CALLER's own copy between Current and Archive — the server
 *  resolves which party's flag that is. Works regardless of job status. */
export function setConversationArchived(
  conversationId: string,
  archived: boolean,
): Promise<Conversation> {
  return api.patch<Conversation>(`/conversations/${encodeURIComponent(conversationId)}/archive`, {
    archived,
  });
}

/**
 * Contractor-only. Also flips the job `Accepted` → `Scheduled` server-side, so
 * the caller should refresh any job it is showing alongside the thread.
 */
export function proposeSchedule(
  conversationId: string,
  body: ScheduleAppointmentRequest,
): Promise<Appointment> {
  return api.post<Appointment>(
    `/conversations/${encodeURIComponent(conversationId)}/schedule`,
    body,
  );
}

export function listAppointments(conversationId: string): Promise<Appointment[]> {
  return api
    .get<AppointmentsResponse>(`/conversations/${encodeURIComponent(conversationId)}/appointments`)
    .then((r) => r.appointments ?? []);
}

/** Either party may cancel. Reverts the job `Scheduled` → `Accepted`. */
export function cancelAppointment(
  conversationId: string,
  appointmentId: string,
  reason?: string,
): Promise<Appointment> {
  return api.post<Appointment>(
    `/conversations/${encodeURIComponent(conversationId)}/appointments/${encodeURIComponent(appointmentId)}/cancel`,
    { reason },
  );
}

/** Contractor-only. Creates a NEW appointment; the old one becomes `Rescheduled`. */
export function rescheduleAppointment(
  conversationId: string,
  appointmentId: string,
  body: ScheduleAppointmentRequest,
): Promise<Appointment> {
  return api.post<Appointment>(
    `/conversations/${encodeURIComponent(conversationId)}/appointments/${encodeURIComponent(appointmentId)}/reschedule`,
    body,
  );
}

/** Contractor-only. Transactionally flips the job to `InProgress`; replay is idempotent. */
export function startAppointment(
  conversationId: string,
  appointmentId: string,
): Promise<{ message: string; appointmentId: string; jobId: string }> {
  return api.post(
    `/conversations/${encodeURIComponent(conversationId)}/appointments/${encodeURIComponent(appointmentId)}/start`,
  );
}
